import moment, { Moment } from "moment";
import Result from "../../deal-builder/utils/result.utils.ts";
import { round } from "../../tools/utils.js";
import { DealBuilderSavedFormat } from "../hooks/useDatabaseAPI.ts";
import { DealStatus } from "../../deal-builder/services/deal-builder.ts";

const activityReportColumns = [
  {
    label: "Bid Date",
    dataName: "bidDate",
    align: "alignCenter",
    width: "5.5%",
    formatter: (value) => moment(value).format("M/D/YYYY"),
  },
  {
    label: "State",
    dataName: "state_of_issuance",
    align: "alignLeft",
    width: "5.5%",
  },
  {
    label: "Issuer",
    dataName: "borrower",
    align: "alignLeft",
    width: "15.5%",
  },
  {
    label: "Issue",
    dataName: "issuance_title",
    align: "alignLeft",
    width: "22%",
  },
  {
    label: "Advisor",
    dataName: "advisor",
    align: "alignRight",
    width: "10%",
  },
  {
    label: "Tax Designation",
    dataName: "tax_designation",
    align: "alignRight",
    width: "7%",
  },
  {
    label: "Amount",
    dataName: "par_amount",
    align: "alignRight",
    width: "7%",
    formatter: (value, isFirst) =>
      isFirst ? `$${value.toLocaleString()}` : value.toLocaleString(),
  },
  {
    label: "Rating Grade",
    dataName: "rating_assumption",
    align: "alignRight",
    width: "5.5%",
  },
  // {
  //   label: "Avg. Life",
  //   dataName: "averageLife",
  //   align: "alignRight",
  //   width: "4.5%",
  //   formatter: (value) => value ? value.toFixed(1) : "-",
  // },
  {
    label: "Coupon",
    dataName: "bid_rate",
    align: "alignRight",
    width: "4.5%",
    formatter: (value) => value ? `${round(value * 100, 2).toFixed(2)}%` : "-",
  },
  {
    label: "Cover Bid",
    dataName: "winningBid",
    align: "alignRight",
    width: "4.5%",
    formatter: (value) =>
      value !== undefined ? `${round(value * 100, 2).toFixed(2)}%` : "-",
  },
  {
    label: "Variance To Market",
    dataName: "variance",
    align: "alignRight",
    width: "8.5%",
    formatter: (value, isFirst) => {
      let returnValue = Math.abs(value).toLocaleString();
      if (value < 0) {
        returnValue = `(${returnValue})`;
      }
      if (isFirst) {
        returnValue = `$${returnValue}`;
      }
      return returnValue;
    },
  },
];

export function generateReportData(
  allDeals: DealBuilderSavedFormat[],
  reportDateRange: [Moment, Moment]
): Result<Object> {
  const activityReportDeals = allDeals.filter((deal) => {
    const bidDate = moment(deal.bid_date);
    return (
      bidDate.isSameOrAfter(reportDateRange[0].startOf("day")) &&
      bidDate.isSameOrBefore(reportDateRange[1].endOf("day"))
    );
  });

  if (activityReportDeals.length === 0) {
    return {
      valid: false,
      error: "No deals found within the specified date range",
    };
  }

  let won: any[] = [];
  let lost: any[] = [];
  const wonTotalsAndAverages = {
    advisor: "TOTALS/AVERAGES",
    parAmount: 0,
    averageLife: 0,
    bidRate: 0,
    winningBid: 0,
    variance: 0,
  };
  const lostTotalsAndAverages = {
    advisor: "TOTALS/AVERAGES",
    parAmount: 0,
    averageLife: 0,
    bidRate: 0,
    winningBid: 0,
    variance: 0,
  };
  let coverBidCount = 0;
  let winningBidCount = 0;

  activityReportDeals.forEach((saved_deal) => {
    const bidRate = saved_deal.bid_rate || 0.04;
    const allBids = [
      saved_deal.bid_rate,
      ...saved_deal.other_bids.map((x) => x[1]),
    ];
    const sortedBids = allBids.sort((a, b) => a - b);

    const { average_life, variance } = saved_deal;
    
    const winningBid = sortedBids[0] - bidRate;
    if (saved_deal.deal_status === DealStatus.WON) {
      won.push({ ...saved_deal, bidRate, average_life, winningBid, variance });
      wonTotalsAndAverages.parAmount += saved_deal.par_amount;
      wonTotalsAndAverages.averageLife += average_life;
      wonTotalsAndAverages.bidRate += bidRate;
      coverBidCount++;
      wonTotalsAndAverages.winningBid += winningBid;
      wonTotalsAndAverages.variance += variance || 0;
    } else if (saved_deal.deal_status === DealStatus.LOST) {
      lost.push({ ...saved_deal, bidRate, average_life, winningBid, variance });
      lostTotalsAndAverages.parAmount += saved_deal.par_amount;
      lostTotalsAndAverages.averageLife += average_life;
      lostTotalsAndAverages.bidRate += bidRate;
      winningBidCount++;
      lostTotalsAndAverages.winningBid += winningBid;
      lostTotalsAndAverages.variance += variance || 0;
    }
  });

  wonTotalsAndAverages.averageLife = round(
    wonTotalsAndAverages.averageLife / won.length,
    2
  );
  wonTotalsAndAverages.bidRate = round(
    wonTotalsAndAverages.bidRate / won.length,
    0
  );
  wonTotalsAndAverages.winningBid = round(
    wonTotalsAndAverages.winningBid / coverBidCount,
    2
  );
  lostTotalsAndAverages.averageLife = round(
    lostTotalsAndAverages.averageLife / lost.length,
    2
  );
  lostTotalsAndAverages.bidRate = round(
    lostTotalsAndAverages.bidRate / lost.length,
    0
  );
  lostTotalsAndAverages.winningBid = round(
    lostTotalsAndAverages.winningBid / winningBidCount,
    2
  );

  const activityReportData = {
    title: "Public Finance Deal Activity Report",
    dateRange: reportDateRange,
    sections: [
      {
        title: "WON",
        color: "#aad08e",
        rows: won.sort(
          (a, b) => moment(a.bid_date).valueOf() - moment(b.bid_date).valueOf()
        ),
        totalsAndAverages: wonTotalsAndAverages,
        columns: activityReportColumns,
      },
      {
        title: "LOST",
        color: "#ddebf7",
        rows: lost.sort(
          (a, b) => moment(a.bid_date).valueOf() - moment(b.bid_date).valueOf()
        ),
        totalsAndAverages: lostTotalsAndAverages,
        columns: [
          ...activityReportColumns.slice(0, 10),
          { ...activityReportColumns[10], label: "Winning Bid" },
          ...activityReportColumns.slice(11, activityReportColumns.length),
        ],
      },
    ],
  };

  return { valid: true, body: activityReportData };
}
